import React from 'react';
import { Outlet, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import styles from './styles/CurrentPage.module.scss';

// Context.
import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

// Components.
import { AuthenticationWrapper } from './AuthenticationWrapper';
import Header from './Header';
import Navigation, { ParentNavLinkProps } from './Navigation';
import LandingPage from '../LandingPage/LandingPage';
import Login from '../LandingPage/Login';
import MyInfo from '../MyInfo/MyInfo';
import Faq from '../Contact/Faq';
import NewsContainer from '../News/NewsContainer';
import Article from '../News/Article';
import Alert from '../Common/Alert';
import Box from '../Common/Box';
import Loading from '../Common/Loading';
import Modal from '../Common/LoginModal';
import ContactPage from '../Contact/ContactPage';
import NordPoolSpotGraph from '../Graphs/NordPoolSpotGraph';
import Footer from './Footer';
import ContractsWrapper from './ContractsWrapper';
import ConsumptionPageWrapper from './ConsumptionPageWrapper';
import Contracts from '../Contracts/Contracts';
import Billing from '../Billing/Billing';
import { ManageContractsWrapper } from '../Contracts/ManageContracts/ManageContractsWrapper';
import Consumption from '../Consumption/Consumption';
import SpotPriceFixing from '../Contracts/ManageContracts/SpotPriceFixing';
import ChargingHistory from '../EvCharging/ChargingHistory';
import ContractsAndTags from '../EvCharging/ContractsAndTags';
import ReportIssue from '../EvCharging/ReportIssue';
import ConsumptionImpactGraph from '../Graphs/ConsumptionImpactGraph';
import EvCharging from '../EvCharging/EvCharging';
// Actions.
import { fetchConfiguration, fetchContract, fetchEVChargerInfo } from '../../actions';
// Utils.
import { inIframe } from '../../utils/login';
import { contractIsOtherType, PriceFixTariffNo } from '../../utils/contracts';
import { contractIsEvCharging } from '../../utils/evCharging';

type CurrentPageProps = {
  singleComponent: boolean;
};

const CurrentPage: React.FunctionComponent<CurrentPageProps> = (props: CurrentPageProps) => {
  const { t } = useTranslation();
  const { users, basicData, config, selectedMeteringPoint, selectedMeteringPointContract, maintenance } = OmaLummeApp.useState();
  const { activeTag, activeSite } = EvChargingApp.useState();
  const dispatch = OmaLummeApp.useDispatch();
  const dispatchEV = EvChargingApp.useDispatch();

  const [userHasToken, setUserHasToken] = React.useState(false);
  const [navOpen, toggleNavOpen] = React.useState(false);
  const [userSwapOpen, toggleUserSwapOpen] = React.useState(false);
  const [redirectUser, setRedirectUser] = React.useState(false);
  const [evChargerInfoUser, setEvChargerInfoUser] = React.useState(0);
  const { singleComponent } = props;

  // If the user has 3rd party cookies blocked then we need to redirect for logins.
  // As such if we have the param "code" then we want to run the same code that we would
  // if we had it as an iframe.
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const param = params.get('code');

  const isIframe = inIframe(singleComponent);

  const evChargingContract = basicData ? basicData.find((d) => contractIsEvCharging(d)) : undefined;

  React.useEffect(() => {
    async function fetchConfig(): Promise<void> {
      const fetchedConfig = await fetchConfiguration();

      if (fetchedConfig) {
        dispatch(['changeConfig', fetchedConfig]);
      }
    }
    fetchConfig();
  }, []);

  React.useEffect(() => {
    setEvChargerInfoUser(0);
  }, [users.activeUser.userId]);

  React.useEffect(() => {
    async function fetchContractData(): Promise<void> {
      dispatch(['changeLoadingContracts', true]);
      const contractBasicData =
        basicData && basicData.length > 0
          ? basicData.find((d) => d.GSRN === selectedMeteringPoint?.meteringPointId && parseInt(d.contractNo) === selectedMeteringPoint.contractNo)
          : undefined;
      if (contractBasicData) {
        const fetchedContract = await fetchContract(parseInt(contractBasicData.customerNo), users.activeUser.userId, contractBasicData.contractNo);

        if (fetchedContract && fetchedContract.contractCode) {
          dispatch(['changeSelectedMeteringPointContract', fetchedContract]);
          dispatch(['changeLoadingContracts', false]);
        } else {
          dispatch(['changeSelectedMeteringPointContract', undefined]);
          dispatch(['changeLoadingContracts', false]);
        }
      } else {
        dispatch(['changeSelectedMeteringPointContract', undefined]);
        dispatch(['changeLoadingContracts', false]);
      }
    }
    fetchContractData();
  }, [basicData, selectedMeteringPoint]);

  React.useEffect(() => {
    let unMounted = false;

    async function getEVChargerInfo(): Promise<void> {
      if (config?.evCharging === true && evChargerInfoUser !== users.activeUser.userId && evChargingContract !== undefined && !unMounted) {
        dispatchEV(['changeEvChargerInfo', undefined]);
        dispatchEV(['changeEvChargerInfoLoading', true]);
        dispatchEV(['changeActiveTag', '']);
        dispatchEV(['changeActiveSite', '']);
        setEvChargerInfoUser(users.activeUser.userId);
        const chargerInfoData = await fetchEVChargerInfo(parseInt(evChargingContract.customerNo), users.activeUser.userId);

        if (chargerInfoData.sites || chargerInfoData.tags) {
          dispatchEV(['changeEvChargerInfo', chargerInfoData]);
          dispatchEV(['changeEvChargerInfoLoading', false]);
          if (chargerInfoData.tags && activeTag === '') {
            const firstTag = chargerInfoData.tags[0].idTag;
            const localStorageTag = window.localStorage.getItem('EvChargingTag');
            if (localStorageTag && chargerInfoData.tags.find((t) => t.idTag === localStorageTag)) {
              dispatchEV(['changeActiveTag', localStorageTag]);
            } else {
              dispatchEV(['changeActiveTag', firstTag]);
              window.localStorage.setItem('EvChargingTag', firstTag);
            }
          }
          if (chargerInfoData.sites && activeSite === '') {
            const firstSite = chargerInfoData.sites[0].groupId.toString();
            const localStorageSite = window.localStorage.getItem('EvChargingSite');
            if (localStorageSite && chargerInfoData.sites.find((s) => s.groupId.toString() === localStorageSite)) {
              dispatchEV(['changeActiveSite', localStorageSite]);
            } else {
              dispatchEV(['changeActiveSite', firstSite]);
              window.localStorage.setItem('EvChargingSite', firstSite);
            }
          }
        }
      } else if (evChargerInfoUser !== users.activeUser.userId && evChargingContract === undefined) {
        dispatchEV(['changeEvChargerInfo', undefined]);
        dispatchEV(['changeEvChargerInfoLoading', false]);
        dispatchEV(['changeActiveTag', '']);
        dispatchEV(['changeActiveSite', '']);
      }
    }
    if (!unMounted) {
      getEVChargerInfo();
    }

    return (): void => {
      unMounted = true;
    };
  }, [selectedMeteringPointContract]);

  // Done like this for translations to be easily added.
  const linkItems: (ParentNavLinkProps | undefined)[] = [
    {
      key: 'home',
      text: t('header:navigation.homeLink'),
      url: '/',
    },
    {
      key: 'myinfo',
      text: t('header:navigation.myInfoLink'),
    },
    {
      key: 'billing',
      text: t('header:navigation.billingLink'),
    },
    {
      key: 'contracts',
      text: t('header:navigation.contractsLink'),
    },
    {
      key: 'electricity',
      text: t('header:navigation.electricityLink'),
      url: '',
      subItems: [
        selectedMeteringPointContract && !contractIsOtherType(selectedMeteringPointContract)
          ? {
              key: 'consumption',
              text: t('header:navigation.consumptionLink'),
            }
          : undefined,
        {
          key: 'nordpool',
          text: t('header:navigation.nordpoolLink'),
        },
        config?.consumptionImpact === true && selectedMeteringPointContract?.isFlexible
          ? {
              key: 'consumptionImpact',
              text: t('header:navigation.consumptionImpactLink'),
            }
          : undefined,
        config?.priceFixing === true &&
        selectedMeteringPointContract?.deliveries &&
        selectedMeteringPointContract.deliveries.filter((d) => d.tariffNo === PriceFixTariffNo && new Date(d.tariffFromDate) <= new Date()).length > 0
          ? {
              key: 'spotPriceFixing',
              text: t('header:navigation.spotPriceFixingLink'),
            }
          : undefined,
        config?.evCharging === true && evChargingContract !== undefined
          ? { key: 'evCharging', text: t('header:navigation.evChargingLink') }
          : undefined,
      ],
    },

    {
      key: 'customerService',
      text: t('header:navigation.customerServiceLink'),
    },
    {
      key: 'news',
      text: t('header:navigation.newsLink'),
    },
  ];

  const userIdentified = users.activeUser.userName !== '' ? true : false;

  const iframeHideCookieInformation = `#hs-eu-cookie-confirmation {display: none;}`;

  // This change means that when redirecting back to the page the whole page acts as a spinner.
  if (isIframe || param) {
    let iframeComponent = <Loading />;

    if (!userHasToken) {
      iframeComponent = <Login userHasToken={userHasToken} setUserHasToken={setUserHasToken} />;
    }
    return iframeComponent;
  } else {
    return (
      <div className={`${styles.wrapper} ${!userIdentified ? styles.register : !singleComponent ? styles.grid : ''}`}>
        {redirectUser && <Modal />}
        {!singleComponent && (
          <a className="skip-link" href="#main-content">
            {t('common:general.skiplink')}
          </a>
        )}
        {!singleComponent && userIdentified && !maintenance?.active && (
          <Header nav={{ navOpen, toggleNavOpen }} userSwap={{ userSwapOpen, toggleUserSwapOpen }} />
        )}
        {!singleComponent && userIdentified && !maintenance?.active && (
          <Navigation links={linkItems} nav={{ navOpen, toggleNavOpen }} userSwap={{ userSwapOpen, toggleUserSwapOpen }} />
        )}
        {singleComponent && <style>{iframeHideCookieInformation}</style>}

        <div id="main-content" className={`${styles.content} ${singleComponent ? `embed ${styles.singleComponent}` : ''}`}>
          <AuthenticationWrapper setRedirectUser={setRedirectUser} userHasToken={userHasToken} setUserHasToken={setUserHasToken}>
            <ContractsWrapper>
              <ConsumptionPageWrapper>
                <Routes>
                  <Route index element={<LandingPage />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="contracts" element={<Contracts />} />
                  <Route path="contracts/manage" element={<ManageContractsWrapper />} />
                  <Route path="myinfo" element={<MyInfo />} />
                  <Route path="consumption" element={<Consumption />} />
                  <Route
                    path="consumptionImpact"
                    element={
                      <Box>
                        <ConsumptionImpactGraph />
                      </Box>
                    }
                  />
                  <Route
                    path="nordpool"
                    element={
                      <Box>
                        <NordPoolSpotGraph />
                      </Box>
                    }
                  />
                  <Route path="customerService" element={<Faq activeUser={users.activeUser} />}>
                    <Route path=":category" element={<Faq activeUser={users.activeUser} />} />
                  </Route>
                  <Route path="customerService/contact" element={<ContactPage />} />
                  <Route path="news" element={<NewsContainer />}>
                    <Route path="benefits" element={<NewsContainer />} />
                    <Route path="articles" element={<NewsContainer />} />
                  </Route>
                  <Route path="article/:slug" element={<Article />} />
                  <Route path="alert/:slug" element={<Alert />} />
                  <Route path="embed/nordpool" element={<NordPoolSpotGraph embed={singleComponent} />} />
                  <Route path="embed/priceFixOffers" element={<SpotPriceFixing embed={singleComponent} />} />
                  <Route path="spotPriceFixing" element={<SpotPriceFixing />}>
                    <Route path="new" element={<SpotPriceFixing />} />
                    <Route path="current" element={<SpotPriceFixing />} />
                    <Route path="upcoming" element={<SpotPriceFixing />} />
                    <Route path="history" element={<SpotPriceFixing />} />
                  </Route>

                  <Route path="evCharging" element={<Outlet />}>
                    <Route index element={<EvCharging />} />
                    <Route path="history" element={<ChargingHistory />}>
                      <Route path=":id" element={<ChargingHistory />} />
                    </Route>
                    <Route path="contractsAndTags" element={<ContractsAndTags />} />
                    <Route path="reportIssue" element={<ReportIssue />} />
                  </Route>
                </Routes>
              </ConsumptionPageWrapper>
            </ContractsWrapper>
          </AuthenticationWrapper>
        </div>
        {!singleComponent && !maintenance?.active && <Footer />}
      </div>
    );
  }
};

export default CurrentPage;
